import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';

type HolidayName = 'christmas' | 'newyears' | 'valentines' | 'stpatricks' | 'july4th' | 'halloween' | 'thanksgiving' | null;

interface HolidaySettings {
  holiday_name: string;
  display_name: string;
  is_enabled: boolean;
  force_state: 'auto' | 'force_on' | 'force_off';
  start_month_day: string | null;
  end_month_day: string | null;
  priority: number;
  icon: string | null;
}

interface HolidayContextType {
  activeHoliday: HolidayName;
  isLoading: boolean;
  holidays: HolidaySettings[];
  refreshHolidays: () => Promise<void>;
}

const HolidayContext = createContext<HolidayContextType | undefined>(undefined);

// Calculate the day after Thanksgiving (4th Thursday of November + 1)
function getDayAfterThanksgiving(year: number): Date {
  const november = new Date(year, 10, 1); // November 1st
  const dayOfWeek = november.getDay();
  // Find first Thursday
  const firstThursday = dayOfWeek <= 4 ? 1 + (4 - dayOfWeek) : 1 + (11 - dayOfWeek);
  // Fourth Thursday
  const thanksgiving = firstThursday + 21;
  return new Date(year, 10, thanksgiving + 1); // Day after
}

function isDateInRange(startMD: string | null, endMD: string | null, holidayName: string): boolean {
  if (!startMD || !endMD) return false;
  
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;
  const currentDay = now.getDate();
  
  // Special handling for Christmas - starts day after Thanksgiving
  if (holidayName === 'christmas') {
    const dayAfterThanksgiving = getDayAfterThanksgiving(currentYear);
    const christmasEnd = new Date(currentYear, 11, 26); // December 26
    return now >= dayAfterThanksgiving && now <= christmasEnd;
  }
  
  const [startMonth, startDay] = startMD.split('-').map(Number);
  const [endMonth, endDay] = endMD.split('-').map(Number);
  
  // Handle year wrap (e.g., Dec 27 - Jan 2)
  if (endMonth < startMonth || (endMonth === startMonth && endDay < startDay)) {
    // Range wraps around year end
    const startDate = new Date(currentYear, startMonth - 1, startDay);
    const endDateNextYear = new Date(currentYear + 1, endMonth - 1, endDay);
    const endDateThisYear = new Date(currentYear, endMonth - 1, endDay);
    
    // Check if we're in the end-of-year portion or start-of-year portion
    return now >= startDate || now <= endDateThisYear;
  }
  
  // Normal range within same year
  const startDate = new Date(currentYear, startMonth - 1, startDay);
  const endDate = new Date(currentYear, endMonth - 1, endDay);
  
  return now >= startDate && now <= endDate;
}

function determineActiveHoliday(holidays: HolidaySettings[]): HolidayName {
  // Sort by priority (lower = higher priority)
  const sorted = [...holidays].sort((a, b) => (a.priority || 10) - (b.priority || 10));
  
  for (const holiday of sorted) {
    if (!holiday.is_enabled) continue;
    
    // Handle force states
    if (holiday.force_state === 'force_on') {
      return holiday.holiday_name as HolidayName;
    }
    if (holiday.force_state === 'force_off') {
      continue;
    }
    
    // Auto mode - check date range
    if (isDateInRange(holiday.start_month_day, holiday.end_month_day, holiday.holiday_name)) {
      return holiday.holiday_name as HolidayName;
    }
  }
  
  return null;
}

export function HolidayProvider({ children }: { children: ReactNode }) {
  const [holidays, setHolidays] = useState<HolidaySettings[]>([]);
  const [activeHoliday, setActiveHoliday] = useState<HolidayName>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchHolidays = async () => {
    try {
      const { data, error } = await supabase
        .from('holiday_settings')
        .select('*')
        .order('priority', { ascending: true });

      if (error) throw error;

      const holidayData = (data || []) as HolidaySettings[];
      setHolidays(holidayData);
      setActiveHoliday(determineActiveHoliday(holidayData));
    } catch (error) {
      // Holiday decorations are optional UI flair. A failed fetch (offline,
      // Supabase outage, missing table) should never contaminate the
      // production console — Lighthouse flags any `console.error` on page
      // load as a "Best Practices" regression. Swallow silently; the UI
      // just renders without decorations in that case.
      if (import.meta.env.DEV) {
        const msg = error instanceof Error ? error.message : String(error);
        console.debug('[HolidayContext] holiday_settings fetch failed:', msg);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
  }, []);

  const refreshHolidays = async () => {
    await fetchHolidays();
  };

  return (
    <HolidayContext.Provider value={{ activeHoliday, isLoading, holidays, refreshHolidays }}>
      {children}
    </HolidayContext.Provider>
  );
}

export function useHoliday() {
  const context = useContext(HolidayContext);
  if (context === undefined) {
    throw new Error('useHoliday must be used within a HolidayProvider');
  }
  return context;
}
